import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ProfilePhoto from "@/components/ProfilePhoto";
import { CandidateBadges } from "@/components/CandidateBadges";
import { categoryLabel } from "@/lib/constants";
import { Heart, HeartOff, Star, MapPin, Briefcase } from "lucide-react";
import { toast } from "sonner";

/** Client-only favorites section (spec §12). */
export default function ClientFavorites() {
    const [favs, setFavs] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = () => {
        setLoading(true);
        api.get("/client/favorites")
            .then((r) => setFavs(r.data))
            .catch(() => setFavs([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { refresh(); }, []);

    const remove = async (candidateUserId) => {
        try {
            await api.delete(`/client/favorites/${candidateUserId}`);
            toast.success("Retiré des favoris");
            refresh();
        } catch (_) {
            toast.error("Erreur");
        }
    };

    return (
        <section className="sd-card p-6 mb-8" data-testid="client-favorites-section">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="overline flex items-center gap-2">
                        <Heart size={12} className="text-[#F5A623] fill-[#F5A623]" />
                        Favoris
                    </div>
                    <h2 className="font-display text-xl">
                        Vos professionnels favoris {favs.length > 0 && <span className="text-sm text-slate-400 font-normal">({favs.length})</span>}
                    </h2>
                    <p className="text-xs text-slate-500 mt-1 max-w-lg">
                        Retrouvez les professionnels avec qui vous avez déjà travaillé ou que vous avez repérés.
                        Vous pourrez bientôt les inviter en priorité sur vos nouvelles missions.
                    </p>
                </div>
            </div>

            {loading && (
                <div className="text-sm text-slate-400 text-center py-6">Chargement…</div>
            )}

            {!loading && favs.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-8" data-testid="client-favorites-empty">
                    Aucun favori pour l'instant. Utilisez le cœur sur une candidature pour ajouter un professionnel à cette liste.
                </div>
            )}

            {!loading && favs.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4">
                    {favs.map((f) => (
                        <div
                            key={f.id}
                            className="rounded-xl border border-slate-200 bg-white p-4 flex gap-3"
                            data-testid={`favorite-card-${f.candidate_user_id}`}
                        >
                            <div className="shrink-0">
                                {f.candidate?.has_portrait ? (
                                    <ProfilePhoto candidateUserId={f.candidate_user_id} size={56} />
                                ) : (
                                    <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold">
                                        {(f.candidate?.first_name || "?").charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start gap-2">
                                    <div className="min-w-0">
                                        <div className="font-semibold text-[#0B192C] text-sm truncate">
                                            {f.candidate?.first_name} {f.candidate?.last_name}
                                        </div>
                                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                                            <span className="inline-flex items-center gap-1">
                                                <Briefcase size={11} />
                                                {categoryLabel(f.candidate?.primary_job) || "—"}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin size={11} />
                                                {f.candidate?.city || "—"}
                                            </span>
                                            {f.candidate?.rating != null && f.candidate?.rating_count > 0 && (
                                                <span className="inline-flex items-center gap-1 text-[#F5A623] font-bold">
                                                    <Star size={11} className="fill-[#F5A623]" />
                                                    {Number(f.candidate.rating).toFixed(1)}
                                                    <span className="text-slate-400 font-normal">({f.candidate.rating_count})</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => remove(f.candidate_user_id)}
                                        className="text-slate-400 hover:text-red-500 shrink-0"
                                        title="Retirer des favoris"
                                        data-testid={`remove-fav-${f.candidate_user_id}`}
                                    >
                                        <HeartOff size={16} />
                                    </button>
                                </div>
                                <div className="mt-2">
                                    <CandidateBadges badges={f.candidate?.badges || []} size="xs" />
                                </div>
                                {f.note && (
                                    <div className="mt-2 text-[11px] text-slate-500 italic bg-slate-50 rounded px-2 py-1">
                                        “{f.note}”
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
